/**
 * The live handoff console.
 *
 * A second seat on the bridge that already runs over SMS to the owner's phone. Both work at
 * once, whichever answers first clears the clock. The two composer modes are the two things
 * the SMS path already does, named in full: Reply goes to the customer verbatim, Instruct is
 * a private note that Ellie phrases.
 */
import { useState } from 'react';
import { Card, Pill, Skeleton, buttonStyle } from '@/features/customers/temp-ui';
import { useHandoff, useHandoffAction, useHandoffs } from './api';

export function HandoffList({ onOpen }: { onOpen: (id: number) => void }) {
  const handoffs = useHandoffs();
  if (handoffs.isLoading) return <Skeleton shape="text" lines={4} />;
  const items = handoffs.data?.items ?? [];
  return (
    <Card title="Live handoffs">
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((handoff) => (
          <li key={handoff.handoff_id} style={{ padding: 'var(--space-2) 0', borderBottom: 'var(--border-width) solid var(--border)' }}>
            <button type="button" style={buttonStyle()} onClick={() => onOpen(handoff.handoff_id)}>
              #{handoff.handoff_id}
            </button>
            <Pill tone={handoff.status === 'pending' ? 'crit' : handoff.status === 'active' ? 'warn' : 'neutral'}>
              {handoff.status}
            </Pill>
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}
              {handoff.customer_first ?? 'anonymous session'}
              {handoff.auto_ticket_in_seconds !== null
                ? ` · auto-tickets in ${handoff.auto_ticket_in_seconds}s`
                : ''}
            </span>
          </li>
        ))}
        {items.length === 0 ? <li style={{ color: 'var(--text-faint)' }}>No handoffs in the last seven days.</li> : null}
      </ul>
    </Card>
  );
}

export function HandoffWorkspace({ handoffId }: { handoffId: number }) {
  const handoff = useHandoff(handoffId);
  const act = useHandoffAction(handoffId);
  const [mode, setMode] = useState<'reply' | 'instruct'>('reply');
  const [body, setBody] = useState('');

  if (handoff.isLoading) return <Skeleton shape="text" lines={8} />;
  if (!handoff.data) return <Card title="Handoff">That handoff could not be loaded.</Card>;

  const { handoff: row, messages } = handoff.data;

  return (
    <Card
      title={`Handoff #${row.handoff_id}`}
      action={
        <span>
          <Pill tone={row.status === 'pending' ? 'crit' : 'warn'}>{row.status}</Pill>{' '}
          {row.auto_ticket_in_seconds !== null ? (
            <Pill tone="warn">auto-tickets in {row.auto_ticket_in_seconds}s</Pill>
          ) : null}
        </span>
      }
    >
      <ol style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 360, overflowY: 'auto' }}>
        {messages.map((message) => (
          <li key={message.message_id} style={{ padding: 'var(--space-2) 0' }}>
            <span className="caps-label" style={{ color: 'var(--text-faint)' }}>
              {message.sender} · {message.created_at.slice(11, 16)}
            </span>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{message.body}</p>
          </li>
        ))}
      </ol>

      <div
        style={{
          marginTop: 'var(--space-3)',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-card)',
          border: mode === 'instruct' ? '2px dashed var(--warn)' : 'var(--border-width) solid var(--border)',
          background: mode === 'instruct' ? 'var(--warn-bg)' : 'var(--surface-2)',
        }}
      >
        <div role="tablist" aria-label="Handoff composer mode" style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'reply'}
            onClick={() => setMode('reply')}
            style={buttonStyle(mode === 'reply' ? 'primary' : 'ghost')}
          >
            Reply (verbatim)
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'instruct'}
            onClick={() => setMode('instruct')}
            style={buttonStyle(mode === 'instruct' ? 'primary' : 'ghost')}
          >
            Instruct Ellie
          </button>
        </div>
        {mode === 'instruct' ? (
          <p className="caps-label" style={{ color: 'var(--warn)' }}>
            Ellie will phrase this. Not sent verbatim.
          </p>
        ) : null}
        <textarea
          aria-label={mode === 'reply' ? 'Reply to the customer' : 'Instruct Ellie'}
          value={body}
          rows={3}
          onChange={(event) => setBody(event.currentTarget.value)}
          style={{ display: 'block', width: '100%', padding: 'var(--space-2)', marginTop: 'var(--space-2)' }}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)', flexWrap: 'wrap' }}>
          <button
            type="button"
            style={buttonStyle('primary')}
            disabled={body.trim() === '' || act.isPending}
            onClick={() => act.mutate({ action: mode, body }, { onSuccess: () => setBody('') })}
          >
            Send
          </button>
          <button type="button" style={buttonStyle()} onClick={() => act.mutate({ action: 'convert' })}>
            Convert to ticket
          </button>
          <button
            type="button"
            style={buttonStyle('danger')}
            onClick={() => act.mutate({ action: 'close', reason: 'agent_close' })}
          >
            Close
          </button>
        </div>
      </div>
    </Card>
  );
}
